<script setup lang="ts">
import BaseIcon from '@/components/ui/BaseIcon.vue';
import type { IconName } from '@/libs/icons';

withDefaults(
  defineProps<{
    label?: string;
    /** Lucide icon name, before the label */
    leadingIcon?: IconName;
    size?: 'medium' | 'large';
    /** Lucide icon name, after the label */
    trailingIcon?: IconName;
    type?: 'primary' | 'brand' | 'secondary' | 'outline' | 'ghost';
  }>(),
  {
    label: undefined,
    leadingIcon: undefined,
    size: 'medium',
    trailingIcon: undefined,
    type: 'secondary',
  },
);
</script>

<template>
  <button class="action-button" :class="[`action-button--${type}`, `action-button--${size}`]" type="button">
    <BaseIcon v-if="leadingIcon" :name="leadingIcon" class="action-button__icon" />
    <span v-if="label || $slots.default" class="action-button__label">
      <slot>{{ label }}</slot>
    </span>
    <BaseIcon v-if="trailingIcon" :name="trailingIcon" class="action-button__icon" />
  </button>
</template>

<style lang="scss">
.action-button {
  align-items: center;
  border: none;
  border-radius: $radius-pill;
  color: $color-neutral-highest;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  gap: 6px;
  justify-content: center;

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }

  &__label {
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    white-space: nowrap;
  }

  &--medium {
    height: 40px;
    padding: $spacing-3 $spacing-4;

    .action-button__icon {
      font-size: 16px;
    }
  }

  &--large {
    height: 52px;
    padding: $spacing-4;

    .action-button__icon {
      font-size: 20px;
    }
  }

  &--primary {
    background-color: $color-neutral-highest;
    color: $color-white;

    &:active {
      background-color: rgba($color-neutral-highest, 0.9);
    }
  }

  // Dark brand green, reserved for buttons floating over the map
  &--brand {
    background-color: $color-primary-darkest;
    color: $color-white;

    &:active {
      background-color: rgba($color-primary-darkest, 0.9);
    }
  }

  &--secondary {
    background-color: $color-neutral-lower;

    &:active {
      background-color: $color-neutral-low;
    }
  }

  &--outline {
    background-color: $color-white;
    border: 1px solid $color-border-medium;

    &:active {
      background-color: $color-neutral-low;
    }
  }

  &--ghost {
    background-color: transparent;

    &:active {
      background-color: $color-neutral-low;
    }
  }
}
</style>
